import _ from 'lodash';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import templateRowStyles from './TemplateRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import TemplateRow from './TemplateRow';
import ListContent from '../composites/ListContent';
import AddTemplateDialog from './AddTemplateDialog';
import EditRoutineTemplate from './EditRoutineTemplate';
import { fetchTemplateById, fetchTemplatesPageByGymId } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { usePaginatedListQuery } from '../../hooks/usePaginatedListQuery';
import { removeItemFromPaginatedQueries } from '../../api/pagination';
import {
  useSessionAction,
  useUserAction,
  useSessionData,
  useUnsavedChanges,
} from '../../providers';
import { useErrorContext } from '../../providers/errorProvider';
import { GymType } from '../../constants';
import { getEmptyTemplate } from '../../utils/templates';

import type { TrainingRoutineTemplate, WithOptionalId } from '../../types';

const templateListTitles = [
  'Workout Name',
  'Exercises',
  'Created By',
  'Last Edit',
  'Options',
];

const RoutineTemplates: React.FC = () => {
  const { loginTrainer, gym, trainers, templates } = useSessionData();
  const { ensureExerciseLibraryLoaded } = useSessionAction();
  const { createRoutineTemplate, updateTemplate, deleteTemplate } =
    useUserAction();
  const { confirmExitIfNeeded } = useUnsavedChanges();
  const { setGlobalError } = useErrorContext();
  const queryClient = useQueryClient();
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState(false);
  const [editedTemplate, setEditedTemplate] =
    useState<WithOptionalId<TrainingRoutineTemplate> | null>(null);

  const initialTemplateRef =
    useRef<WithOptionalId<TrainingRoutineTemplate> | null>(null);
  const saveRef = useRef<() => Promise<void> | void>(() => { });

  useEffect(() => {
    if (!loginTrainer) {
      return;
    }
    void ensureExerciseLibraryLoaded();
  }, [ensureExerciseLibraryLoaded, loginTrainer]);

  useEffect(() => {
    if (editedTemplate && !initialTemplateRef.current) {
      initialTemplateRef.current = _.cloneDeep(editedTemplate);
    } else if (!editedTemplate) {
      initialTemplateRef.current = null;
    }
  }, [editedTemplate]);

  const isGymOfTypeSchool = gym?.type === GymType.School;

  const templatesListQuery = usePaginatedListQuery<TrainingRoutineTemplate>({
    enabled: Boolean(gym?.id),
    queryKey: (params) =>
      queryKeys.templates.gymPage(gym?.id || '', params),
    queryFn: (params) =>
      fetchTemplatesPageByGymId(gym!.id, params),
  });

  const filteredTemplates = useMemo(() => {
    return templatesListQuery.items
      .map((template) => ({
        ...templates[template.id],
        ...template,
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [templates, templatesListQuery.items]);

  const templatesPagination = {
    page: templatesListQuery.page,
    pageSize: templatesListQuery.pageSize,
    totalElements: templatesListQuery.meta.totalElements,
    totalPages: templatesListQuery.meta.totalPages,
    onPageChange: templatesListQuery.setPage,
    onPageSizeChange: templatesListQuery.setPageSize,
    disabled: templatesListQuery.isFetching,
  };

  const invalidateTemplatesPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['templates'] });
  };

  // Moved above early return to satisfy rules-of-hooks
  const handleEditTemplate = useCallback(
    async (
      newTemplateData: WithOptionalId<TrainingRoutineTemplate>,
    ): Promise<boolean> => {
      if (!loginTrainer) return false;
      const success = newTemplateData.id
        ? await updateTemplate(newTemplateData as TrainingRoutineTemplate)
        : await createRoutineTemplate(newTemplateData, loginTrainer.id);
      if (!success) {
        return false;
      }
      setEditedTemplate(null);
      setIsAddTemplateModalOpen(false);
      invalidateTemplatesPage();
      return true;
    },
    [updateTemplate, createRoutineTemplate, loginTrainer],
  );

  // Moved above early return to satisfy rules-of-hooks
  const handleCloseEditTemplate = useCallback(() => {
    setEditedTemplate(null);
  }, []);

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<FitnessCenterOutlinedIcon />}
        text="No workouts for your role."
      />
    );
  }

  saveRef.current = async () => {
    if (editedTemplate) {
      await handleEditTemplate(editedTemplate);
    }
  };

  const renderEmptyState = () => (
    <EmptyState
      icon={<FitnessCenterOutlinedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add A Workout',
        onClick: () => {
          if (isGymOfTypeSchool) {
            const defaultTemplate = getEmptyTemplate(
              '',
              0,
              '',
              true,
              loginTrainer.id,
            );
            setEditedTemplate(defaultTemplate);
          } else {
            setIsAddTemplateModalOpen(true);
          }
        },
      }}
      text="No Workouts Have Been Added"
    />
  );

  const renderAddTemplateDialog = () => (
    <AddTemplateDialog
      title="Create A Template"
      trainerId={loginTrainer.id}
      addTemplate={setEditedTemplate}
      onClose={() => setIsAddTemplateModalOpen(false)}
      isOpen={isAddTemplateModalOpen}
    />
  );

  const discardTemplate = async (template: TrainingRoutineTemplate) => {
    const success = await deleteTemplate(template);
    if (!success) {
      return;
    }

    removeItemFromPaginatedQueries<TrainingRoutineTemplate>(
      queryClient,
      ['templates'],
      template.id,
    );
    invalidateTemplatesPage();
  };

  const loadTemplateDetails = async (
    template: TrainingRoutineTemplate,
  ): Promise<TrainingRoutineTemplate | null> => {
    const { data, success, error } = await fetchTemplateById(template.id);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error loading workout details');
      return null;
    }
    return data;
  };

  const openEditTemplate = async (template: TrainingRoutineTemplate) => {
    const detailedTemplate = await loadTemplateDetails(template);
    if (!detailedTemplate) return;
    setEditedTemplate(detailedTemplate);
  };

  const duplicateTemplate = async (template: TrainingRoutineTemplate) => {
    const detailedTemplate = await loadTemplateDetails(template);
    if (!detailedTemplate) return;
    const newTemplate = {
      ...detailedTemplate,
      name: `${detailedTemplate.name} - Copy`,
      routine: {
        ...detailedTemplate.routine,
        name: `${detailedTemplate.name} - Copy`,
      },
      id: undefined,
    };
    await createRoutineTemplate(newTemplate, loginTrainer.id);
    invalidateTemplatesPage();
  };

  const renderContent = () => {
    if (editedTemplate) return null;
    return (
      <ListContent
        items={filteredTemplates}
        isLoading={templatesListQuery.isLoading}
        listHeaderClassName={templateRowStyles.listGrid}
        pagination={templatesPagination}
        EmptyStateComponent={renderEmptyState}
        titles={templateListTitles}
        ItemComponent={({ data }) => (
          <TemplateRow
            template={data}
            deleteTemplate={discardTemplate}
            allowDelete={loginTrainer.id === data.trainerIds[0]}
            duplicateTemplate={duplicateTemplate}
            openEditTemplateModal={() => openEditTemplate(data)}
            selected={selectedTemplate === data.id}
            onSelect={() => setSelectedTemplate(data.id)}
            key={data.id}
            trainer={trainers[data.trainerIds[0]]?.trainer}
          />
        )}
      />
    );
  };

  const getDrillInContent = () => {
    if (!editedTemplate) return null;
    return (
      <EditRoutineTemplate
        onSubmit={handleEditTemplate}
        template={editedTemplate}
        setTemplate={
          setEditedTemplate as React.Dispatch<
            React.SetStateAction<WithOptionalId<TrainingRoutineTemplate>>
          >
        }
        onClose={handleCloseEditTemplate}
        isNew={!editedTemplate.id}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredTemplates.length) return '';
    return 'Search for Workouts';
  };

  return (
    <ContentDrill
      title={
        editedTemplate
          ? editedTemplate?.id
            ? 'Edit Workout'
            : 'Add Workout'
          : 'Workout Library'
      }
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Workout',
          onClick: () => {
            if (isGymOfTypeSchool) {
              const defaultTemplate = getEmptyTemplate(
                '',
                0,
                '',
                true,
                loginTrainer.id,
              );
              setEditedTemplate(defaultTemplate);
            } else {
              setIsAddTemplateModalOpen(true);
            }
          },
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      searchQuery={templatesListQuery.searchQuery}
      setSearchQuery={templatesListQuery.setSearchQuery}
      disableSearch={false}
      isDrilledIn={Boolean(editedTemplate)}
      drillInContent={getDrillInContent()}
      onBack={
        editedTemplate
          ? () => confirmExitIfNeeded(handleCloseEditTemplate)
          : undefined
      }
    >
      {renderContent()}
      {!isGymOfTypeSchool && renderAddTemplateDialog()}
    </ContentDrill>
  );
};

export default RoutineTemplates;
